import { z } from 'zod'
import { instantSchema } from '../validators/temporal.validator.js'
import { nullableCalendarDaySchema } from '../validators/calendar-day.validator.js'
import { quoteRequesterRoleSchema } from '../enums/quote-requester-role.enum.js'

/**
 * A request for a quote, as it was sent (docs/vendor-growth.spec.md, "Quote
 * requests"): who asked, for which event, about which packages, and what they
 * said. The vendor got it by email and answers there.
 */
export const quoteRequestDtoSchema = z.object({
  id: z.string().uuid(),
  sentAt: instantSchema,
  vendor: z.object({ vendorId: z.string().uuid(), name: z.string() }),
  requester: z.object({
    name: z.string(),
    email: z.string(),
    role: quoteRequesterRoleSchema,
    /** The planner's company, or the venue for its staff; null for a couple. */
    company: z.string().nullable(),
  }),
  event: z.object({
    /** Null once the event is deleted: the vendor keeps what it was sent. */
    eventId: z.string().uuid().nullable(),
    date: nullableCalendarDaySchema,
    venueName: z.string(),
    guestCount: z.number().int().positive().nullable(),
  }),
  /** By name, as they were when asked about. */
  packages: z.array(z.string()),
  message: z.string(),
})

export type QuoteRequestDto = z.infer<typeof quoteRequestDtoSchema>

/** An event's requests: all of them for its planner and the venue's staff, a couple's own for a couple. */
export const eventQuoteRequestListDtoSchema = z.object({ requests: z.array(quoteRequestDtoSchema) })

export type EventQuoteRequestListDto = z.infer<typeof eventQuoteRequestListDtoSchema>

/** A vendor company's requests, newest first. */
export const vendorQuoteRequestListDtoSchema = z.object({
  requests: z.array(quoteRequestDtoSchema),
  total: z.number().int().nonnegative(),
})

export type VendorQuoteRequestListDto = z.infer<typeof vendorQuoteRequestListDtoSchema>
